/* eslint-disable react/destructuring-assignment */
import { IconButton, InputAdornment, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LockIcon from '@mui/icons-material/Lock'
import { useMemo } from 'react'
import sxs from './_sxs'
import { FormFieldProps } from './_props'
import FormFieldInput from './variants/FormFieldInput'
import FormFieldSwitch from './variants/FormFieldSwitch'
import FormFieldSelect from './variants/FormFieldSelect'
import FormFieldButtonGroup from './variants/FormFieldButtonGroup'
import FormFieldCustom from './variants/FormFieldCustom'
import { DEFAULT_NO_VALUE } from '../_utils/utils'

const FormFieldVariant = <T extends object, O>(props: FormFieldProps<T, O>) => {
    const defaultClearableCheck = props?.status?.valid || props?.status?.error || props?.status?.warning
    const isClearableInput = useMemo(() => {
        if (props.clearable === false) return false

        return defaultClearableCheck ?? props.clearable
    }, [defaultClearableCheck, props.clearable])

    const endAdornmentClear = useMemo(
        () => (
            <InputAdornment position="end" className="endAdornmentClear" sx={{ display: 'none' }}>
                <IconButton onClick={props.onClear}>
                    <CloseRoundedIcon />
                </IconButton>
            </InputAdornment>
        ),
        [props.onClear],
    )

    const endAdornmentLocked = useMemo(
        () =>
            props?.locked && (
                <InputAdornment position="end" className="endAdornmentLocked">
                    <LockIcon
                        sx={{
                            width: '1.3rem',
                            height: '1.3rem',
                            opacity: 0.7,
                        }}
                    />
                </InputAdornment>
            ),
        [props?.locked],
    )

    const endAdornment = useMemo(() => {
        const hasEndAdornment = endAdornmentClear || endAdornmentLocked

        if (!hasEndAdornment) return null

        return (
            <Stack>
                {props.value && isClearableInput ? endAdornmentClear : null}
                {props.locked ? endAdornmentLocked : null}
            </Stack>
        )
    }, [endAdornmentClear, endAdornmentLocked, isClearableInput, props.locked, props.value])

    const readonly = props.readonly || props.locked
    const isDisabled = props.locked || props.disabled

    if (readonly && (props.variant === 'input' || props.variant === 'select')) {
        return (
            <FormFieldInput
                {...props}
                variant="input"
                onChange={undefined}
                placeholder={props.noValueText ?? DEFAULT_NO_VALUE}
                disabled
                clearable={false}
                endAdornment={props.locked ? endAdornmentLocked : null}
            />
        )
    }

    if (props.variant === 'input') {
        return (
            <FormFieldInput
                {...props}
                placeholder={isDisabled ? props.noValueText ?? DEFAULT_NO_VALUE : props.placeholder}
                startAdornment={props.startAdornment}
                endAdornment={endAdornment}
            />
        )
    }

    if (props.variant === 'switch') return <FormFieldSwitch {...props} />

    if (props.variant === 'select') return <FormFieldSelect {...props} placeholder={props.noValueText ?? 'Choisir'} />

    if (props.variant === 'buttonGroup') return <FormFieldButtonGroup {...props} />

    if (props.variant === 'custom')
        return <FormFieldCustom {...props} placeholder={props.noValueText ?? DEFAULT_NO_VALUE} />

    return (
        <Stack sx={sxs.FormFieldErrorContainer}>
            <Typography sx={sxs.FormFieldErrorContainerText}>INVALID VARIANT</Typography>
        </Stack>
    )
}

export default FormFieldVariant
